import React from 'react';
import { Box, List, ListItemButton, ListItemText, Stack, TextField, Tooltip, Typography } from '@mui/material';
import type { PageInfo } from '../../hooks/usePageView';

interface PageNavigatorProps {
  pages: PageInfo[];
  activeIndex: number;
  onJump: (index: number) => void;
}

/**
 * Direct page navigation for a GN in paged view (spec 046 US3, FR-030). SC-011 requires
 * reaching any page in a 15-page notebook in ≤2 interactions — a flat clickable list
 * satisfies that trivially (open + click = 1 interaction from an already-visible list).
 */
const PageNavigator: React.FC<PageNavigatorProps> = ({ pages, activeIndex, onJump }) => {
  if (pages.length <= 1) return null;

  return (
    <Box sx={{ width: 200, borderRight: '1px solid', borderColor: 'divider', overflow: 'auto' }} data-testid="gn-page-navigator">
      <Typography variant="overline" color="text.secondary" sx={{ px: 1.5, pt: 1, display: 'block' }}>
        Pages ({pages.length})
      </Typography>
      <List dense disablePadding>
        {pages.map((page) => (
          <Tooltip key={page.index} title={page.label} placement="right">
            <ListItemButton
              selected={page.index === activeIndex}
              onClick={() => onJump(page.index)}
              data-testid={`gn-page-nav-${page.index}`}
            >
              <ListItemText
                primary={page.label}
                primaryTypographyProps={{ noWrap: true, variant: 'body2' }}
              />
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </Box>
  );
};

export default PageNavigator;

interface PageBreakLabelPromptProps {
  onConfirm: (label: string) => void;
  onCancel: () => void;
}

/** Small inline prompt for naming a new page break (used by the "convert to pages" action). */
export const PageBreakLabelPrompt: React.FC<PageBreakLabelPromptProps> = ({ onConfirm, onCancel }) => {
  const [label, setLabel] = React.useState('');
  return (
    <Stack direction="row" spacing={1} sx={{ p: 1 }}>
      <TextField
        size="small"
        autoFocus
        placeholder="Page label (optional)"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onConfirm(label);
          if (e.key === 'Escape') onCancel();
        }}
      />
    </Stack>
  );
};
